import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useChat } from "@/hooks/use-chat";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Users, Globe, Loader2, MessageCircle, X } from "lucide-react";
import { LanguageSelector } from "@/components/LanguageSelector";
import { detectUserLanguage } from "@/lib/languages";
import { getAuthHeaders } from "@/lib/queryClient";
import { apiUrl } from "@/lib/config";

// --- Theme Color Definitions ---
const LIGHT_GRADIENT_FROM = "hsl(130, 40%, 75%)";
const LIGHT_GRADIENT_TO = "hsl(0, 0%, 98%)";
const SUBTLE_GRADIENT_FROM = "#0A3832";
const SUBTLE_GRADIENT_TO = "#000000";

const THEME_COLORS = {
    "--light-gradient-from": LIGHT_GRADIENT_FROM,
    "--light-gradient-to": LIGHT_GRADIENT_TO,
    "--subtle-gradient-from": SUBTLE_GRADIENT_FROM,
    "--subtle-gradient-to": SUBTLE_GRADIENT_TO,
} as React.CSSProperties;

export default function JoinPage() {
    const [, navigate] = useLocation();
    const { user, loading: authLoading } = useAuth();
    const { joinRoomByInvite } = useChat(user?.uid || null);
    const [inviteCode, setInviteCode] = useState<string>("");
    const [roomInfo, setRoomInfo] = useState<any>(null);
    const [joining, setJoining] = useState(false);
    const [loading, setLoading] = useState(true);
    const [checkingMembership, setCheckingMembership] = useState(true);
    const [showLanguageSelection, setShowLanguageSelection] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState<string>("");
    const [inviteError, setInviteError] = useState<string | null>(null);
    const { toast } = useToast();

    useEffect(() => {
        const path = window.location.pathname;
        const code = path.split("/join/")[1];
        if (code) {
            setInviteCode(code);
            localStorage.setItem("pending_invite_code", code);
            fetchRoomInfo(code);
        } else {
            setLoading(false);
        }
    }, []);

    // Navigate straight to the room. pending_invite_code must be removed in the
    // same handler as navigate(): App.tsx re-stores it on every render while
    // the location is /app/join/*.
    //
    // replace, not push: this is a redirect, so the /app/join/<code> entry must
    // not stay in history. If it does, Back returns here, the already-member
    // check re-runs and immediately redirects forward again — the Back button
    // looks dead for anyone who arrived via an invite link.
    const openRoomInChat = (roomId: string) => {
        localStorage.removeItem("pending_invite_code");
        navigate(`/app/chat/${roomId}`, { replace: true });
    };

    // Already a member (e.g. same invite link clicked again): skip the join
    // card entirely and open the existing chat — zero clicks.
    useEffect(() => {
        if (loading || authLoading) return;
        if (!roomInfo || !user?.uid) {
            setCheckingMembership(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(apiUrl(`/api/users/${user.uid}/rooms`));
                if (res.ok) {
                    const rooms = await res.json();
                    if (!cancelled && Array.isArray(rooms) && rooms.some((r: any) => r.roomId === roomInfo.roomId)) {
                        openRoomInChat(roomInfo.roomId);
                        return;
                    }
                }
            } catch {
                // Pre-check is best-effort; the join button is idempotent anyway
            }
            if (!cancelled) setCheckingMembership(false);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, authLoading, roomInfo, user?.uid]);

    useEffect(() => {
        if (!selectedLanguage) {
            const detectedLang = detectUserLanguage();
            setSelectedLanguage(detectedLang);
        }
    }, [selectedLanguage]);

    const fetchRoomInfo = async (code: string) => {
        try {
            const response = await fetch(apiUrl(`/api/rooms/invite/${code}`));
            if (response.ok) {
                const room = await response.json();
                setRoomInfo(room);
                setInviteError(null);
            } else {
                const errorData = await response.json().catch(() => ({}));
                const message = errorData.error || "This invite link is invalid or has expired.";
                setInviteError(message);
                toast({
                    title: "Invalid Invite",
                    description: message,
                    variant: "destructive",
                });
            }
        } catch {
            const message = "We couldn't load this invite. Check your connection and try again.";
            setInviteError(message);
            toast({
                title: "Error",
                description: message,
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleJoinRoom = async () => {
        const hasCompletedOnboarding = localStorage.getItem("vyzle-chat-onboarded");
        const hasSelectedLanguage = localStorage.getItem("vyzle-chat-language");

        if (!hasCompletedOnboarding || !hasSelectedLanguage) {
            setShowLanguageSelection(true);
            return;
        }

        await proceedWithJoin(hasSelectedLanguage);
    };

    const proceedWithJoin = async (languageToUse: string) => {
        setJoining(true);
        try {
            if (user?.uid && languageToUse) {
                try {
                    await fetch(apiUrl(`/api/users/${user.uid}`), {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json", ...(await getAuthHeaders()) },
                        body: JSON.stringify({ preferredLanguage: languageToUse }),
                    });
                } catch (error) {
                    console.warn("Failed to update language preference:", error);
                }
            }

            const room = await joinRoomByInvite(inviteCode);
            toast({
                title: "Joined Successfully!",
                description: `You've joined ${room.name}`,
            });

            openRoomInChat(room.roomId);
        } catch (error) {
            console.error("Join room error:", error);
            toast({
                title: "Join Failed",
                description: error instanceof Error ? error.message : "Failed to join the chat. Please try again.",
                variant: "destructive",
            });
        } finally {
            setJoining(false);
        }
    };

    const handleLanguageSelect = async (language: string) => {
        setSelectedLanguage(language);
        setShowLanguageSelection(false);
        localStorage.setItem("vyzle-chat-language", language);
        if (user?.uid) {
            localStorage.setItem(`user_language_${user.uid}`, language);
        }
        localStorage.setItem("vyzle-chat-onboarded", "true");
        setTimeout(() => proceedWithJoin(language), 100);
    };

    if (loading || authLoading || (checkingMembership && roomInfo)) {
        return (
            <div
                className="min-h-screen flex items-center justify-center p-4
                   bg-gradient-to-br from-[var(--light-gradient-from)] to-[var(--light-gradient-to)]
                   dark:from-[var(--subtle-gradient-from)] dark:to-[var(--subtle-gradient-to)]"
                style={THEME_COLORS}
            >
                <Card className="w-full max-w-md shadow-xl border-0 bg-white/30 dark:bg-black/30 backdrop-blur-md">
                    <CardHeader className="text-center pb-4">
                        <div className="w-20 h-20 bg-[var(--accent)] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                            <Loader2 className="w-10 h-10 text-white animate-spin" />
                        </div>

                        <CardTitle className="text-2xl font-bold text-foreground">Loading Invite</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-center space-y-4">
                            <p className="text-muted-foreground">{loading ? "Verifying invite link..." : "Setting up your account..."}</p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!inviteCode || !roomInfo) {
        return (
            <div
                className="min-h-screen flex items-center justify-center p-4
                   bg-gradient-to-br from-[var(--light-gradient-from)] to-[var(--light-gradient-to)]
                   dark:from-[var(--subtle-gradient-from)] dark:to-[var(--subtle-gradient-to)]"
                style={THEME_COLORS}
            >
                <Card className="w-full max-w-md shadow-xl border-0 bg-white/30 dark:bg-black/30 backdrop-blur-md">
                    <CardHeader className="text-center pb-4">
                        <div className="w-20 h-20 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Globe className="w-10 h-10 text-red-500" />
                        </div>
                        <CardTitle className="text-2xl font-bold text-foreground">Invalid Invite</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-center space-y-6">
                            <p className="text-muted-foreground">{inviteError || "This invite link is invalid or has expired. Please ask for a new invite link."}</p>
                            <Button
                                onClick={() => {
                                    localStorage.removeItem("pending_invite_code");
                                    navigate("/app/chat");
                                }}
                                className="w-full text-white"
                                style={{ backgroundColor: "var(--accent)" }}
                                size="lg"
                            >
                                <MessageCircle className="w-4 h-4 mr-2" />
                                Go to Vyzle Chat
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div
            style={THEME_COLORS}
            className="min-h-screen flex items-center justify-center p-4 dark:text-white text-gray-900 
                 bg-gradient-to-tl from-[var(--light-gradient-from)] to-[var(--light-gradient-to)]
                 dark:from-[var(--subtle-gradient-from)] dark:to-[var(--subtle-gradient-to)]"
        >
            <Card className="w-full max-w-lg shadow-lg bg-white/30 dark:bg-black/30 backdrop-blur-md border border-white/20">
                <CardHeader className="text-center pb-4">
                    <div className="w-20 h-20 bg-[var(--accent)] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">{roomInfo.type === "group" ? <Users className="w-10 h-10 text-white" /> : <MessageCircle className="w-10 h-10 text-white" />}</div>
                    <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Join Vyzle Chat</CardTitle>
                    <p className="text-gray-600 dark:text-gray-300">You've been invited to join a conversation</p>
                </CardHeader>

                <CardContent className="space-y-6">
                    <div className="rounded-lg p-4 bg-[var(--light-gradient-to)]/50 dark:bg-[var(--subtle-gradient-to)]/40 border border-gray-300 dark:border-white/10">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-[var(--accent)] rounded-full flex items-center justify-center">{roomInfo.type === "group" ? <Users className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-white" />}</div>
                            <div>
                                <h3 className="font-semibold text-gray-900 dark:text-white">{roomInfo.name}</h3>
                                <p className="text-sm text-gray-600 dark:text-gray-300">{roomInfo.type === "group" ? "Group Chat" : "Direct Chat"}</p>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <Button
                            onClick={handleJoinRoom}
                            disabled={joining}
                            className="w-full flex-1 h-10 rounded-xl text-lg font-semibold text-white shadow-none
                         hover:shadow-[0_4px_25px_var(--accent)]
                         active:translate-y-0.5 active:shadow-inner transition-all duration-300"
                            style={{ backgroundColor: "var(--accent)" }}
                        >
                            {joining ? (
                                <>
                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                    Joining...
                                </>
                            ) : (
                                <>
                                    <Users className="w-5 h-5 mr-2" />
                                    Join Chat
                                </>
                            )}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {showLanguageSelection && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
                    onClick={() => setShowLanguageSelection(false)}
                >
                    <Card
                        className="relative w-full max-w-3xl max-h-[90vh] shadow-lg bg-white/30 dark:bg-black/30 backdrop-blur-md border border-white/20"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setShowLanguageSelection(false)}
                            className="absolute right-3 top-3 p-1 rounded-full text-gray-500 hover:text-gray-900 dark:hover:text-white"
                            aria-label="Close"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <CardHeader className="border-b border-white/10">
                            <CardTitle className="text-center text-2xl font-bold text-gray-900 dark:text-white">
                                <Globe className="w-6 h-6 inline-block mr-2 text-primary" />
                                Choose Your Language
                            </CardTitle>
                            <p className="text-center text-gray-600 dark:text-gray-300">Select your preferred language for chat translations</p>
                        </CardHeader>
                        <CardContent className="p-6 overflow-hidden">
                            <LanguageSelector onLanguageSelect={handleLanguageSelect} selectedLanguage={selectedLanguage} className="w-full" />
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}
